import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { compositeAPI, unitsOfMeasureAPI } from '../api/endpoints.js'
import { useToast } from '../contexts/ToastContext'

export const useQuotationByToken = () => {
  const { linkToken } = useParams()
  const { success, error } = useToast()
  const [searchTerm, setSearchTerm] = useState('')
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [quotationInfo, setQuotationInfo] = useState(null)
  const [isAlreadyResponded, setIsAlreadyResponded] = useState(false)
  const [quotationData, setQuotationData] = useState({
    minimumOrderValue: '',
    paymentTerm: '',
    payment: [], // Array de métodos de pagamento
    deliveryTerm: '',
    deliveryTimeUnit: 'hours',
    deliveryDays: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    },
    items: []
  })

  // Buscar informações da cotação baseado no link_token
  useEffect(() => {
    if (!linkToken) return

    const loadQuotationData = async () => {
      try {
        // Verificar se já foi respondida
        const responseKey = `quotation_response_${linkToken}`
        const existingResponse = localStorage.getItem(responseKey)
        let savedData = null
        if (existingResponse) {
          setIsAlreadyResponded(true)
          savedData = JSON.parse(existingResponse)
        }

        // Buscar dados da cotação pelo token
        const quotationData = await compositeAPI.getQuotationByToken(linkToken)
        
        if (!quotationData.success) {
          console.error('Token de cotação não encontrado:', linkToken)
          error('Cotação não encontrada ou token inválido')
          return
        }

        const quotationInfo = quotationData.data
        
        // Debug: verificar estrutura dos dados
        console.log('Estrutura dos itens do backend:', quotationInfo.items)
        
        // Mapear itens com informações completas
        const itemsWithDetails = quotationInfo.items?.map(qi => {
          const mappedItem = {
            id: qi.id,
            itemId: qi.item_id,
            name: qi.item?.name || qi.description || 'Item não encontrado',
            notes: qi.item?.notes || qi.notes || qi.description || '', // Marca do produto (exibição)
            brand: qi.item?.notes || qi.notes || qi.description || '', // Campo editável de marca
            available: false,
            quantity: qi.quantity || '',
            unitOfMeasureId: qi.unit_id || '',
            unitPrice: '',
            observations: ''
          }
          
          // Debug: mostrar dados de cada item
          console.log(`Item ${qi.id}:`, {
            originalData: qi,
            mappedNotes: mappedItem.notes,
            mappedQuantity: mappedItem.quantity
          })
          
          return mappedItem
        }) || []

        setQuotationInfo(quotationInfo)

        // Se já foi respondida, carregar dados salvos
        if (savedData) {
          // Limpar paymentTerm se não houver boleto selecionado
          const savedPayments = Array.isArray(savedData.payment) ? savedData.payment : (savedData.payment ? [savedData.payment] : [])
          const cleanedPaymentTerm = savedPayments.includes('boleto') ? (savedData.paymentTerm || '') : ''
          
          setQuotationData({
            ...savedData,
            paymentTerm: cleanedPaymentTerm,
            // Garantir que payment seja sempre um array
            payment: savedPayments,
            items: itemsWithDetails.map(item => {
              const savedItem = savedData.items?.find(si => si.id === item.id)
              return savedItem ? { ...item, ...savedItem } : item
            })
          })
        } else {
          setQuotationData(prev => ({
            ...prev,
            items: itemsWithDetails
          }))
        }
      } catch (err) {
        console.error('Erro ao carregar dados da cotação:', err)
        error('Erro ao carregar dados da cotação')
      }
    }

    loadQuotationData()
  }, [linkToken, error])

  const [units, setUnits] = useState([])
  
  // Carregar unidades de medida
  useEffect(() => {
    const loadUnits = async () => {
      try {
        const response = await unitsOfMeasureAPI.getAll()
        if (response.success) {
          setUnits(response.data || [])
        }
      } catch (err) {
        console.error('Erro ao carregar unidades:', err)
      }
    }
    loadUnits()
  }, [])

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.id === unitId)
    return unit ? unit.abbreviation : 'un'
  }

  const filteredItems = quotationData.items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const updateFormData = (field, value) => {
    clearFieldError(field)
    setQuotationData(prev => ({ ...prev, [field]: value }))
  }

  const updateDeliveryDay = (day) => {
    clearFieldError('deliveryDays')
    setQuotationData(prev => ({
      ...prev,
      deliveryDays: {
        ...prev.deliveryDays,
        [day]: !prev.deliveryDays[day]
      }
    }))
  }

  const updatePaymentMethod = (method) => {
    clearFieldError('payment')
    setQuotationData(prev => {
      const currentPayments = prev.payment
      const isSelected = currentPayments.includes(method)
      
      let newPayments
      if (isSelected) {
        // Remove o método se já estiver selecionado
        newPayments = currentPayments.filter(p => p !== method)
      } else {
        // Adiciona o método se não estiver selecionado
        newPayments = [...currentPayments, method]
      }
      
      return {
        ...prev,
        payment: newPayments
      }
    })
  }

  const updateItem = (itemId, field, value) => {
    // Limpar erro específico do campo do item
    setErrors(prev => {
      const newErrors = { ...prev }
      if (newErrors[`item_${itemId}`]) {
        delete newErrors[`item_${itemId}`][field]
        if (Object.keys(newErrors[`item_${itemId}`]).length === 0) {
          delete newErrors[`item_${itemId}`]
        }
      }
      delete newErrors.items // Limpar erro geral de itens
      return newErrors
    })
    
    setQuotationData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }))
  }

  const clearFieldError = (field) => {
    setErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[field]
      return newErrors
    })
  }

  const validateField = (field, value) => {
    const newErrors = {}
    
    switch (field) {
      case 'minimumOrderValue':
        const minValue = parseFloat(value)
        if (!value || minValue < 0) {
          newErrors.minimumOrderValue = 'Valor mínimo deve ser maior ou igual a 0'
        }
        break
      case 'paymentTerm':
        // Validar apenas se boleto estiver selecionado
        if (quotationData.payment.includes('boleto')) {
          const paymentTerm = parseInt(value)
          if (!value || paymentTerm <= 0) {
            newErrors.paymentTerm = 'Prazo de pagamento deve ser maior que 0 quando boleto for selecionado'
          }
        }
        break
      case 'payment':
        if (!Array.isArray(value) || value.length === 0) {
          newErrors.payment = 'Selecione pelo menos um método de pagamento'
        }
        break
      case 'deliveryTerm':
        const deliveryTerm = parseInt(value)
        if (!value || deliveryTerm <= 0) {
          newErrors.deliveryTerm = 'Prazo de entrega deve ser maior que 0'
        }
        break
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }))
  }

  const validateForm = () => {
    const newErrors = {}

    // Validar valor mínimo
    const minValue = parseFloat(quotationData.minimumOrderValue)
    if (!quotationData.minimumOrderValue || minValue < 0) {
      newErrors.minimumOrderValue = 'Valor mínimo deve ser maior ou igual a 0'
    }

    // Validar prazo de pagamento (apenas se boleto for selecionado)
    if (quotationData.payment.includes('boleto')) {
      const paymentTerm = parseInt(quotationData.paymentTerm)
      if (!quotationData.paymentTerm || paymentTerm <= 0) {
        newErrors.paymentTerm = 'Prazo de pagamento deve ser maior que 0 quando boleto for selecionado'
      }
    }

    // Validar método de pagamento
    if (!Array.isArray(quotationData.payment) || quotationData.payment.length === 0) {
      newErrors.payment = 'Selecione pelo menos um método de pagamento'
    }

    // Validar prazo de entrega
    const deliveryTerm = parseInt(quotationData.deliveryTerm)
    if (!quotationData.deliveryTerm || deliveryTerm <= 0) {
      newErrors.deliveryTerm = 'Prazo de entrega deve ser maior que 0'
    }

    // Validar dias de entrega
    const selectedDays = Object.values(quotationData.deliveryDays).some(day => day)
    if (!selectedDays) {
      newErrors.deliveryDays = 'Selecione pelo menos um dia de entrega'
    }

    // Validar itens disponíveis
    const availableItems = quotationData.items.filter(item => item.available)
    if (availableItems.length === 0) {
      newErrors.items = 'Marque pelo menos um item como disponível'
    }

    // Validar campos dos itens disponíveis
    availableItems.forEach(item => {
      if (!item.unitOfMeasureId) {
        newErrors[`item_${item.id}`] = { ...newErrors[`item_${item.id}`], unitOfMeasureId: 'Selecione uma unidade de medida' }
      }
      if (!item.brand?.trim()) {
        newErrors[`item_${item.id}`] = { ...newErrors[`item_${item.id}`], brand: 'Marca é obrigatória' }
      }
      if (!item.unitPrice || parseFloat(item.unitPrice) <= 0) {
        newErrors[`item_${item.id}`] = { ...newErrors[`item_${item.id}`], unitPrice: 'Valor unitário deve ser maior que 0' }
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      // Preparar dados para envio
      const responseData = {
        quotation_supplier_id: quotationInfo.quotationSupplier?.id,
        response_date: new Date().toISOString().split('T')[0],
        total_value: quotationData.items
          .filter(item => item.available)
          .reduce((total, item) => total + parseFloat(item.unitPrice || 0), 0),
        minimum_order_value: parseFloat(quotationData.minimumOrderValue) || 0,
        payment_terms_days: quotationData.payment.includes('boleto') ? (parseInt(quotationData.paymentTerm) || 0) : 0,
        payment: quotationData.payment, // Array de métodos
        delivery_time_value: parseInt(quotationData.deliveryTerm) || 1,
        delivery_time_unit: quotationData.deliveryTimeUnit || 'days',
        delivery_days: Object.keys(quotationData.deliveryDays)
          .filter(day => quotationData.deliveryDays[day])
          .map(day => {
            const dayMap = {
              monday: 'mon',
              tuesday: 'tue', 
              wednesday: 'wed',
              thursday: 'thu',
              friday: 'fri',
              saturday: 'sat',
              sunday: 'sun'
            }
            return dayMap[day] || day
          }),
        observations: 'Resposta enviada via sistema',
        items: quotationData.items
          .map(item => ({
            quotation_item_id: item.id,
            unit_id: item.available ? (parseInt(item.unitOfMeasureId) || 1) : 1, // Default para não disponíveis
            quantity: parseFloat(item.quantity) || 1, // Usar quantidade original da cotação como número
            brand: item.available ? (item.brand || 'N/A') : 'N/A', // Usar marca editável ou N/A
            unit_price: item.available ? (parseFloat(item.unitPrice) || 0.01) : 0, // Preço 0 para indisponíveis
            available: item.available,
            notes: item.observations || ''
          }))
      }

      // Log dos dados para debug
      console.log('Dados sendo enviados:', JSON.stringify(responseData, null, 2))
      console.log('Dados originais dos itens:', quotationInfo.items)
      console.log('Dados mapeados dos itens:', quotationData.items)

      // Enviar resposta via API
      const response = await compositeAPI.submitSupplierResponse(responseData)
      
      if (response.success) {
        // Salvar resposta no localStorage para controle local
        const responseKey = `quotation_response_${linkToken}`
        const localData = {
          ...quotationData,
          submittedAt: new Date().toISOString(),
          linkToken,
          apiResponse: response.data
        }
        localStorage.setItem(responseKey, JSON.stringify(localData))

        // Marcar como respondida
        setIsAlreadyResponded(true)
        
        success('Cotação enviada com sucesso!')
      } else {
        throw new Error(response.message || 'Erro ao enviar cotação')
      }
    } catch (err) {
      console.error('Erro ao enviar cotação:', err)
      error(err.message || 'Erro ao enviar cotação. Tente novamente.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    quotationData,
    quotationInfo,
    searchTerm,
    filteredItems,
    errors,
    isSubmitting,
    isAlreadyResponded,
    linkToken,
    units,
    updateFormData,
    updateDeliveryDay,
    updatePaymentMethod,
    updateItem,
    setSearchTerm,
    handleSubmit,
    getUnitName,
    validateField
  }
}